import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import type { FormInstance } from 'antd';
import { DownOutlined, RobotOutlined, UpOutlined } from '@ant-design/icons';
import {
  buildEffectiveRegistry,
  getRegistryVersion,
  subscribeRegistryChanges,
  type CovariateDefinition,
} from '../config/covariateRegistry';
import { stripConflictingNeonatalCovariates } from '../constants/populationGroups';
import { useAgent } from '../contexts/AgentContext';
import {
  caseAPI,
  drugAPI,
  intakeAPI,
  loadCovariateDefinitions,
  patientAPI,
} from '../services/api';
import type {
  AgentFieldSource,
  Case,
  IntakeContextResponse,
  IntakeCovariateItem,
  Patient,
} from '../types';
import { toAgentFieldKey, toFormFieldKey } from '../utils/fieldKeys';
import { getPopulationTagColor } from '../utils/clinicalTagPalette';
import {
  buildPatientDerivedPatch,
  formatDerivedCovariateFormulaName,
  todayIsoDate,
} from '../utils/patientIntakeDerived';
import { makeCovariateBucketsExclusive } from '../utils/covariateBuckets';
import { formatErrorMessage } from '../utils/errorHandler';

const { Text } = Typography;
const { Option } = Select;
const BASE_FIELD_KEYS = new Set(['patient_id', 'drug_name', 'diagnosis', 'birth_date', 'current_date']);
const STATIC_COVARIATE_KEYS = new Set(['gender', 'height', 'weight', 'age_years']);
const REFERENCE_POPULATION_LABELS: Record<string, string> = {
  neonate: '新生儿',
  infant: '婴儿',
  child: '儿童',
  adolescent: '青少年',
  adult: '成人',
};

const STYLES = `
  .patient-intake {
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: #0b2545;
  }
  .patient-intake__card {
    position: relative;
    border: 1px solid #e2ebf3;
    border-radius: 18px;
    background:
      radial-gradient(circle at top left, rgba(15, 98, 254, 0.06), transparent 28%),
      linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    box-shadow: 0 10px 24px rgba(11, 37, 69, 0.05);
    overflow: hidden;
  }
  .patient-intake__card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 16px 0;
  }
  .patient-intake__card-title {
    color: #061529;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }
  .patient-intake__card-desc { margin-top: 3px; color: #667085; font-size: 11px; line-height: 1.55; }
  .patient-intake__card-body { padding: 12px 16px 14px; }
  .patient-intake__summary-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 26px;
    padding: 0 8px;
    border-radius: 999px;
    border: 1px solid #dbe6f1;
    background: rgba(248, 251, 255, 0.92);
    color: #344054;
    font-size: 10px;
    font-weight: 700;
  }
  .patient-intake__summary-chip strong { color: #0f172a; font-weight: 800; }
  .patient-intake__derived-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 8px;
  }
  .patient-intake__derived-item {
    border: 1px solid #e3ebf4;
    border-radius: 14px;
    background: linear-gradient(180deg, #f9fbff 0%, #f4f8fd 100%);
    padding: 10px 12px;
  }
  .patient-intake__derived-label { color: #344054; font-size: 11px; font-weight: 700; }
  .patient-intake__derived-value { margin-top: 4px; color: #0f172a; font-size: 15px; font-weight: 800; }
  .patient-intake__derived-meta { margin-top: 6px; color: #667085; font-size: 11px; line-height: 1.45; }
  .patient-intake__empty {
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px dashed #d5e0eb;
    background: linear-gradient(180deg, #fcfdff 0%, #f7fbff 100%);
    color: #667085;
    font-size: 12px;
    line-height: 1.55;
  }
  .patient-intake__actions {
    position: sticky;
    bottom: 4px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e3ebf4;
    border-radius: 16px;
    background: rgba(255,255,255,.96);
    backdrop-filter: blur(14px);
    box-shadow: 0 10px 22px rgba(11, 37, 69, 0.07);
  }
  .patient-intake .ant-form-item-label > label { color: #445266; font-size: 11px; font-weight: 700; }
  .patient-intake .ant-form-item {
    margin-bottom: 12px;
  }
  .patient-intake .ant-input,
  .patient-intake .ant-input-number,
  .patient-intake .ant-select-selector,
  .patient-intake .ant-picker {
    border-radius: 12px !important;
    border-color: #d9e5f0 !important;
    min-height: 38px;
    box-shadow: none !important;
  }
  .patient-intake .ant-input:focus,
  .patient-intake .ant-input-focused,
  .patient-intake .ant-input-number-focused,
  .patient-intake .ant-select-focused .ant-select-selector,
  .patient-intake .ant-picker-focused {
    border-color: #0f62fe !important;
    box-shadow: 0 0 0 3px rgba(15, 98, 254, 0.12) !important;
  }
  .patient-intake .ant-form-item-explain,
  .patient-intake .ant-form-item-extra {
    margin-top: 6px;
    white-space: normal;
    overflow: visible;
  }
  .patient-intake__field-note {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6px 8px;
    width: 100%;
    padding: 6px 8px;
    border-radius: 10px;
    border: 1px solid #e6edf5;
    background: linear-gradient(180deg, #fbfdff 0%, #f6fafe 100%);
    color: #667085;
    font-size: 11px;
    line-height: 1.45;
  }
  .patient-intake__field-note--warning {
    border-color: #ffd8bf;
    background: linear-gradient(180deg, #fffaf5 0%, #fff4eb 100%);
    color: #ad4e00;
  }
  .patient-intake__field-note--confirmed {
    border-color: #d7e9db;
    background: linear-gradient(180deg, #f7fcf8 0%, #eef8f1 100%);
    color: #2f6b3d;
  }
  .patient-intake__field-note-text {
    flex: 1 1 180px;
    min-width: 0;
  }
  .patient-intake__field-note-action.ant-btn {
    margin-left: auto;
    flex: 0 0 auto;
    padding: 0;
    height: auto;
    font-size: 12px;
    font-weight: 700;
  }
  .patient-intake__submit.ant-btn {
    min-width: 180px;
    height: 40px;
    border-radius: 12px;
    font-weight: 700;
    box-shadow: 0 10px 22px rgba(15, 98, 254, 0.18);
  }
  .patient-intake__clear.ant-btn, .patient-intake__agent.ant-btn, .patient-intake__toggle.ant-btn {
    height: 34px;
    border-radius: 12px;
    font-size: 12px;
  }
  @media (max-width: 1024px) {
    .patient-intake__card-head, .patient-intake__card-body { padding-left: 12px; padding-right: 12px; }
    .patient-intake__actions { flex-direction: column; }
    .patient-intake__submit.ant-btn, .patient-intake__clear.ant-btn, .patient-intake__agent.ant-btn { width: 100%; }
  }
  @media (max-width: 768px) {
    .patient-intake__derived-grid { grid-template-columns: 1fr; }
  }
`;

interface PatientFormProps {
  onSubmit: (patient: Patient, caseData: Case) => void;
  externalForm?: FormInstance;
  onClearWorkspace?: () => void;
}

interface DrugOption {
  drug_id: string;
  drug_name: string;
  drug_name_en?: string;
}

interface DisplayFieldItem extends IntakeCovariateItem {
  sortOrder: number;
}

type DisplayBucketKey = 'nextRequired' | 'conditional' | 'optional';

const createBucketOrderMap = (): Record<DisplayBucketKey, Record<string, number>> => ({
  nextRequired: {},
  conditional: {},
  optional: {},
});

const createBucketOrderCounters = (): Record<DisplayBucketKey, number> => ({
  nextRequired: 0,
  conditional: 0,
  optional: 0,
});

type ResolvedFieldDefinition = {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select';
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  tooltip?: string;
  default_value?: any;
  options?: { label: string; value: any }[];
  companions?: string[];
  derived_from?: string[];
  sort_order?: number;
};

export const resolveCovariatePlaceholder = (
  definition: Pick<ResolvedFieldDefinition, 'placeholder'> | undefined,
  fallback: string,
): string => definition?.placeholder?.trim() || fallback;

const isMissing = (value: unknown) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const coerceCovariateValue = (
  value: unknown,
  definition?: Pick<ResolvedFieldDefinition, 'type'>,
): string | number | boolean | undefined => {
  if (isMissing(value)) return undefined;
  if (definition?.type === 'number') {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (definition?.type === 'boolean') {
    if (typeof value === 'boolean') return value;
    const token = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(token)) return true;
    if (['false', '0', 'no', 'n', 'off'].includes(token)) return false;
    return undefined;
  }
  return value as string | number | boolean;
};

const fieldSourceIsProtected = (source?: AgentFieldSource) => (
  source === 'manual'
  || source === 'confirmed_current'
  || source === 'ai_verified'
  || source === 'ai_extracted'
  || source === 'derived'
);

const definitionFromItem = (
  item: IntakeCovariateItem,
  registryDefinition?: CovariateDefinition,
): ResolvedFieldDefinition => ({
  key: item.key,
  label: item.label || registryDefinition?.label || item.key,
  type: (item.data_type as ResolvedFieldDefinition['type']) || registryDefinition?.type || 'number',
  unit: item.unit || registryDefinition?.unit,
  min: item.min_val ?? registryDefinition?.min,
  max: item.max_val ?? registryDefinition?.max,
  step: item.step_val ?? registryDefinition?.step,
  placeholder: item.placeholder || registryDefinition?.placeholder,
  tooltip: item.tooltip || registryDefinition?.tooltip,
  default_value: item.default_value ?? registryDefinition?.default_value,
  options: (item.options as { label: string; value: any }[] | undefined) || registryDefinition?.options,
  companions: item.companions || registryDefinition?.companions,
  derived_from: item.derived_from || registryDefinition?.derived_from,
  sort_order: item.sort_order ?? ((registryDefinition as any)?.sort_order as number | undefined),
});

const sortItems = (items: DisplayFieldItem[]) => [...items].sort((left, right) => {
  if (left.required !== right.required) return left.required ? -1 : 1;
  if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
  return left.label.localeCompare(right.label, 'zh-CN');
});

const compareDisplayItems = (left: DisplayFieldItem, right: DisplayFieldItem) => {
  if (left.required !== right.required) return left.required ? -1 : 1;
  if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
  return left.label.localeCompare(right.label, 'zh-CN');
};

const PatientForm: React.FC<PatientFormProps> = ({ onSubmit, externalForm, onClearWorkspace }) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const [registryVersion, setRegistryVersion] = useState(() => getRegistryVersion());
  const registry = useMemo(() => buildEffectiveRegistry(), [registryVersion]);
  const [drugs, setDrugs] = useState<DrugOption[]>([]);
  const [drugsLoading, setDrugsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [intakeContext, setIntakeContext] = useState<IntakeContextResponse | null>(null);
  const [contextError, setContextError] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [manualFieldKeys, setManualFieldKeys] = useState<string[]>([]);
  const [manualExtraFieldKeys, setManualExtraFieldKeys] = useState<string[]>([]);
  const [showMoreFields, setShowMoreFields] = useState(true);
  const [formValues, setFormValues] = useState<Record<string, any>>(() => form.getFieldsValue(true));
  const bucketOrderRef = useRef<Record<DisplayBucketKey, Record<string, number>>>(createBucketOrderMap());
  const bucketOrderCounterRef = useRef<Record<DisplayBucketKey, number>>(createBucketOrderCounters());
  const sanitizedFormValues = formValues;
  const drugName = formValues.drug_name;
  const patientId = formValues.patient_id;
  const {
    aiExtractionSnapshot,
    openDrawer,
    pendingFill,
    setPendingFill,
    setAIExtractionSnapshot,
  } = useAgent();

  const syncFormValues = useCallback(() => {
    setFormValues(form.getFieldsValue(true));
  }, [form]);

  const applyLinkedBasePatch = useCallback((changedKeys: string[]) => {
    if (!changedKeys.length) return;

    const patch = buildPatientDerivedPatch(
      form.getFieldsValue(true),
      changedKeys.map((key) => toFormFieldKey(key)),
    );
    if (!Object.keys(patch).length) return;

    const currentValues = form.getFieldsValue(true);
    const nextPatch = Object.entries(patch).reduce<Record<string, string | number>>((acc, [key, value]) => {
      if (currentValues[key] !== value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (Object.keys(nextPatch).length) {
      form.setFieldsValue(nextPatch);
    }
  }, [form]);

  useEffect(() => subscribeRegistryChanges(setRegistryVersion), []);

  useEffect(() => {
    void loadCovariateDefinitions();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setDrugsLoading(true);
      try {
        const data = await drugAPI.activated();
        if (alive) setDrugs(data.drugs || []);
      } catch {
        if (alive) message.error('药物列表加载失败');
      } finally {
        if (alive) setDrugsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    syncFormValues();
  }, [syncFormValues]);

  useEffect(() => {
    bucketOrderRef.current = createBucketOrderMap();
    bucketOrderCounterRef.current = createBucketOrderCounters();
  }, [drugName, patientId]);

  const stabilizeBucketItems = useCallback((bucket: DisplayBucketKey, items: DisplayFieldItem[]) => {
    const orderMap = bucketOrderRef.current[bucket];
    let nextOrder = bucketOrderCounterRef.current[bucket];
    items.forEach((item) => {
      if (orderMap[item.key] != null) return;
      orderMap[item.key] = nextOrder;
      nextOrder += 1;
    });
    bucketOrderCounterRef.current[bucket] = nextOrder;
    return [...items].sort((left, right) => {
      const leftOrder = orderMap[left.key] ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = orderMap[right.key] ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return compareDisplayItems(left, right);
    });
  }, []);

  useEffect(() => {
    const currentDate = form.getFieldValue('current_date');
    if (currentDate) return;
    form.setFieldValue('current_date', todayIsoDate());
    syncFormValues();
  }, [form, syncFormValues]);

  useEffect(() => {
    if (isMissing(formValues.birth_date) || isMissing(formValues.current_date) || !isMissing(formValues.age_years)) {
      return;
    }
    applyLinkedBasePatch(['birth_date']);
    syncFormValues();
  }, [applyLinkedBasePatch, formValues.age_years, formValues.birth_date, formValues.current_date, syncFormValues]);

  useEffect(() => {
    if (!pendingFill) return;
    const currentValues = form.getFieldsValue(true) as Record<string, any>;
    const previousFieldSources = aiExtractionSnapshot?.field_sources || {};
    const nextValues = Object.entries(pendingFill.values || {}).reduce<Record<string, any>>((acc, [rawKey, value]) => {
      const canonicalKey = toAgentFieldKey(rawKey);
      const formKey = toFormFieldKey(rawKey);
      const isManual = manualFieldKeys.includes(canonicalKey) || previousFieldSources[canonicalKey] === 'manual';
      if (!(isManual && !isMissing(currentValues[formKey]))) acc[formKey] = value;
      return acc;
    }, {});
    const clearPatch = (pendingFill.clearKeys || []).reduce<Record<string, undefined>>((acc, rawKey) => {
      const canonicalKey = toAgentFieldKey(rawKey);
      const formKey = toFormFieldKey(rawKey);
      const isManual = manualFieldKeys.includes(canonicalKey) || previousFieldSources[canonicalKey] === 'manual';
      if (!(isManual && !isMissing(currentValues[formKey]))) acc[formKey] = undefined;
      return acc;
    }, {});
    if (Object.keys(nextValues).length || Object.keys(clearPatch).length) {
      form.setFieldsValue({ ...clearPatch, ...nextValues });
      applyLinkedBasePatch(Object.keys(nextValues));
      syncFormValues();
    }
    setPendingFill(null);
  }, [aiExtractionSnapshot, applyLinkedBasePatch, form, manualFieldKeys, pendingFill, setPendingFill, syncFormValues]);

  useEffect(() => {
    let alive = true;
    if (!drugName) {
      setIntakeContext(null);
      setContextError('');
      setManualFieldKeys([]);
      return () => {
        alive = false;
      };
    }
    const covariates = stripConflictingNeonatalCovariates(Object.fromEntries(
      Object.entries(formValues).filter(([key, value]) => !BASE_FIELD_KEYS.has(key) && !isMissing(value)),
    ));
    const timer = window.setTimeout(async () => {
      setContextLoading(true);
      setContextError('');
      try {
        const next = await intakeAPI.context({
          drug_id: String(drugName),
          patient_id: typeof patientId === 'string' ? patientId : undefined,
          birth_date: typeof formValues.birth_date === 'string' ? formValues.birth_date : undefined,
          current_date: typeof formValues.current_date === 'string' ? formValues.current_date : undefined,
          covariates,
        });
        if (alive) setIntakeContext(next);
      } catch (error) {
        if (alive) {
          setIntakeContext(null);
          setContextError(
            formatErrorMessage(error, '协变量要求读取失败，请重试。'),
          );
        }
      } finally {
        if (alive) setContextLoading(false);
      }
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [drugName, formValues, patientId]);

  const selectedDrug = useMemo(
    () => drugs.find((item) => item.drug_id === drugName) || null,
    [drugName, drugs],
  );

  const availableManualExtraOptions = useMemo(() => (
    (intakeContext?.optional_covariates || [])
      .filter((item) => item?.key && !BASE_FIELD_KEYS.has(item.key) && !STATIC_COVARIATE_KEYS.has(item.key))
      .map((item) => {
        const definition = definitionFromItem(item, registry[item.key]);
        return {
          label: definition.unit ? `${definition.label} (${definition.unit})` : definition.label,
          value: item.key,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label, 'zh-CN'))
  ), [intakeContext, registry]);

  const manualExtraPreviewOptions = useMemo(
    () => availableManualExtraOptions.slice(0, 8),
    [availableManualExtraOptions],
  );

  const stageKeySet = useMemo(() => new Set([
    ...(intakeContext?.required_covariates || []).map((item) => item.key),
    ...(intakeContext?.optional_covariates || []).map((item) => item.key),
  ]), [intakeContext]);

  const blockingKeySet = useMemo(
    () => new Set((intakeContext?.next_required_covariates || []).map((item) => item.key)),
    [intakeContext],
  );

  const activeConflictItems = useMemo(
    () => (intakeContext?.conflict_covariates || []).filter((item) => stageKeySet.has(item.key)),
    [intakeContext, stageKeySet],
  );

  const localReadinessStatus = useMemo(() => {
    if (!intakeContext || !intakeContext.ready_for_model_selection) return 'needs_info';
    if (activeConflictItems.length > 0) return 'review_needed';
    return 'match_ready';
  }, [activeConflictItems.length, intakeContext]);

  useEffect(() => {
    if (!intakeContext) return;
    const covariates = stripConflictingNeonatalCovariates(Object.fromEntries(
      Object.entries(formValues).filter(([key, value]) => !BASE_FIELD_KEYS.has(key) && !isMissing(value)),
    ));
    const populatedSnapshotKeys = [
      ...(formValues.drug_name ? ['drug_name'] : []),
      ...(formValues.patient_id ? ['patient_id'] : []),
      ...(formValues.birth_date ? ['birth_date'] : []),
      ...(formValues.current_date ? ['current_date'] : []),
      ...Object.keys(covariates),
    ];
    setAIExtractionSnapshot((prev) => ({
      ...(prev || {
        candidate_fields: [],
        recommended_apply_keys: [],
        applied_keys: [],
        field_patches: [],
        missing_required: [],
        conflicts: [],
        follow_up_questions: [],
      }),
      readiness_status: localReadinessStatus,
      intake_context: intakeContext,
      missing_required: intakeContext.next_required_covariates.map((item) => ({ key: item.key, label: item.label, source_tags: [] })),
      conflicts: activeConflictItems.map((item) => ({ key: item.key, reason: item.reason || 'candidate_model_conflict' })),
      field_sources: {
        ...Object.fromEntries(Object.entries(prev?.field_sources || {}).filter(([key]) => populatedSnapshotKeys.includes(key))),
        ...Object.fromEntries(manualFieldKeys.filter((key) => populatedSnapshotKeys.includes(key)).map((key) => [key, 'manual' as AgentFieldSource])),
      },
    }));
  }, [
    activeConflictItems,
    formValues,
    intakeContext,
    localReadinessStatus,
    manualFieldKeys,
    setAIExtractionSnapshot,
  ]);

  const bucketedFields = useMemo(() => {
    const nextMap = new Map<string, DisplayFieldItem>();
    const conditionalMap = new Map<string, DisplayFieldItem>();
    const optionalMap = new Map<string, DisplayFieldItem>();
    const addItem = (target: Map<string, DisplayFieldItem>, item: IntakeCovariateItem, requiredOverride?: boolean) => {
      if (!item?.key || BASE_FIELD_KEYS.has(item.key) || STATIC_COVARIATE_KEYS.has(item.key)) return;
      const definition = definitionFromItem(item, registry[item.key]);
      const nextItem: DisplayFieldItem = {
        ...item,
        label: definition.label,
        unit: definition.unit,
        data_type: item.data_type || definition.type,
        options: item.options || definition.options,
        min_val: item.min_val ?? definition.min,
        max_val: item.max_val ?? definition.max,
        step_val: item.step_val ?? definition.step,
        placeholder: item.placeholder || definition.placeholder,
        tooltip: item.tooltip || definition.tooltip,
        default_value: item.default_value ?? definition.default_value,
        derived_from: item.derived_from || definition.derived_from,
        companions: item.companions || definition.companions,
        required: requiredOverride ?? item.required,
        sortOrder: Number(definition.sort_order ?? 9999),
      };
      target.set(item.key, nextItem);
      (definition.companions || []).forEach((companionKey) => {
        if (BASE_FIELD_KEYS.has(companionKey) || target.has(companionKey) || !registry[companionKey]) return;
        target.set(companionKey, {
          key: companionKey,
          label: registry[companionKey].label,
          unit: registry[companionKey].unit,
          data_type: registry[companionKey].type,
          options: registry[companionKey].options,
          placeholder: registry[companionKey].placeholder,
          tooltip: registry[companionKey].tooltip,
          default_value: registry[companionKey].default_value,
          derived_from: registry[companionKey].derived_from,
          companions: registry[companionKey].companions,
          required: Boolean(requiredOverride ?? item.required),
          requirement_level: item.requirement_level,
          model_ids: item.model_ids,
          required_model_ids: item.required_model_ids,
          optional_model_ids: item.optional_model_ids,
          missing: item.missing,
          reason: item.reason,
          sortOrder: Number((registry[companionKey] as any)?.sort_order ?? 9999),
        });
      });
    };
    (intakeContext?.required_covariates || []).forEach((item) => {
      if (
        item.requirement_level === 'required_now'
        || item.requirement_level === 'required_for_selected_model'
        || (!item.requirement_level && item.required)
      ) {
        addItem(nextMap, item, true);
      } else if (item.requirement_level === 'required_for_candidate_models') {
        addItem(conditionalMap, item, false);
      }
    });
    (intakeContext?.optional_covariates || []).forEach((item) => {
      if (!nextMap.has(item.key) && !conditionalMap.has(item.key)) addItem(optionalMap, item, false);
    });
    Object.entries(sanitizedFormValues).forEach(([key, value]) => {
      if (BASE_FIELD_KEYS.has(key) || STATIC_COVARIATE_KEYS.has(key) || isMissing(value) || nextMap.has(key) || conditionalMap.has(key) || optionalMap.has(key) || !registry[key]) return;
      optionalMap.set(key, {
        key,
        label: registry[key].label,
        unit: registry[key].unit,
        data_type: registry[key].type,
        options: registry[key].options,
        placeholder: registry[key].placeholder,
        tooltip: registry[key].tooltip,
        default_value: registry[key].default_value,
        derived_from: registry[key].derived_from,
        companions: registry[key].companions,
        required: false,
        requirement_level: 'optional',
        model_ids: [],
        required_model_ids: [],
        optional_model_ids: [],
        missing: false,
        reason: '已手工录入',
        sortOrder: Number((registry[key] as any)?.sort_order ?? 9999),
      });
    });
    manualExtraFieldKeys.forEach((key) => {
      if (BASE_FIELD_KEYS.has(key) || STATIC_COVARIATE_KEYS.has(key) || nextMap.has(key) || conditionalMap.has(key) || optionalMap.has(key) || !registry[key]) return;
      optionalMap.set(key, {
        key,
        label: registry[key].label,
        unit: registry[key].unit,
        data_type: registry[key].type,
        options: registry[key].options,
        placeholder: registry[key].placeholder,
        tooltip: registry[key].tooltip,
        default_value: registry[key].default_value,
        derived_from: registry[key].derived_from,
        companions: registry[key].companions,
        required: false,
        requirement_level: 'optional',
        model_ids: [],
        required_model_ids: [],
        optional_model_ids: [],
        missing: true,
        reason: '手工补录字段',
        sortOrder: Number((registry[key] as any)?.sort_order ?? 9999),
      });
    });
    const exclusiveBuckets = makeCovariateBucketsExclusive({
      nextRequired: Array.from(nextMap.values()),
      conditional: Array.from(conditionalMap.values()),
      optional: Array.from(optionalMap.values()),
    });
    return {
      nextRequired: stabilizeBucketItems('nextRequired', exclusiveBuckets.nextRequired),
      conditional: stabilizeBucketItems('conditional', exclusiveBuckets.conditional),
      optional: stabilizeBucketItems('optional', exclusiveBuckets.optional),
    };
  }, [intakeContext, manualExtraFieldKeys, registry, sanitizedFormValues, stabilizeBucketItems]);

  const visibleDefaultableFields = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...bucketedFields.nextRequired,
      ...bucketedFields.conditional,
      ...bucketedFields.optional,
    ].filter((item) => {
      if (!item.key || seen.has(item.key)) return false;
      seen.add(item.key);
      if (item.requirement_level ? item.requirement_level !== 'optional' : item.required) return false;
      return !isMissing(definitionFromItem(item, registry[item.key]).default_value);
    });
  }, [bucketedFields, registry]);

  useEffect(() => {
    if (!visibleDefaultableFields.length) return;

    const currentValues = form.getFieldsValue(true);
    const previousFieldSources = aiExtractionSnapshot?.field_sources || {};
    const patch: Record<string, any> = {};
    const defaultSourcePatch: Record<string, AgentFieldSource> = {};

    visibleDefaultableFields.forEach((item) => {
      const canonicalKey = toAgentFieldKey(item.key);
      const formKey = toFormFieldKey(item.key);
      if (!isMissing(currentValues[formKey])) return;
      if (manualFieldKeys.includes(canonicalKey)) return;
      if (fieldSourceIsProtected(previousFieldSources[canonicalKey])) return;
      if ((intakeContext?.derived_covariates || []).some((derived) => (
        toAgentFieldKey(derived.key) === canonicalKey && !isMissing(derived.value)
      ))) return;

      const definition = definitionFromItem(item, registry[item.key]);
      const defaultValue = coerceCovariateValue(definition.default_value, definition);
      if (defaultValue === undefined) return;
      patch[formKey] = defaultValue;
      defaultSourcePatch[canonicalKey] = 'default';
    });

    if (!Object.keys(patch).length) return;

    form.setFieldsValue(patch);
    setAIExtractionSnapshot((prev) => ({
      ...(prev || {
        candidate_fields: [],
        recommended_apply_keys: [],
        applied_keys: [],
        field_patches: [],
        missing_required: [],
        conflicts: [],
        follow_up_questions: [],
      }),
      field_sources: {
        ...(prev?.field_sources || {}),
        ...defaultSourcePatch,
      },
    }));
    syncFormValues();
  }, [
    aiExtractionSnapshot?.field_sources,
    form,
    intakeContext?.derived_covariates,
    manualFieldKeys,
    registry,
    setAIExtractionSnapshot,
    syncFormValues,
    visibleDefaultableFields,
  ]);

  const canSubmit = Boolean(selectedDrug) && Boolean(patientId) && !contextLoading && Boolean(intakeContext?.ready_for_model_selection);
  const showSummary = Boolean(selectedDrug || contextLoading || intakeContext);

  const summaryStatus = useMemo(() => {
    if (!selectedDrug) return { title: '请选择目标药物后继续录入', description: '系统会按当前录入进度动态展开后续字段。', tagColor: 'default' as const, tagLabel: '待开始' };
    if (contextLoading) return { title: '正在刷新当前阶段与字段', description: '你可以继续手工填写，系统会自动更新下一步需要的信息。', tagColor: 'processing' as const, tagLabel: '更新中' };
    if (contextError) return { title: '协变量要求读取失败', description: '当前不能判断是否满足计算条件。', tagColor: 'error' as const, tagLabel: '不可继续' };
    if (!intakeContext) return { title: '正在准备当前病例的录入上下文', description: '稍后会展示当前必须补齐和建议补齐的字段。', tagColor: 'default' as const, tagLabel: '准备中' };
    if (intakeContext.ready_for_model_selection) return { title: intakeContext.stage_title || '已满足进入模型选择条件', description: '可以进入第 2 步，剩余字段可后续补充。', tagColor: 'success' as const, tagLabel: '可继续' };
    return {
      title: intakeContext.stage_title || '继续补齐关键字段',
      description: intakeContext.next_required_covariates.length ? `请补齐：${intakeContext.next_required_covariates.map((item) => item.label).join('、')}` : '继续录入后系统会自动刷新下一阶段所需字段。',
      tagColor: 'blue' as const,
      tagLabel: '录入中',
    };
  }, [contextError, contextLoading, intakeContext, localReadinessStatus, selectedDrug]);

  const baseFieldMeta = useCallback((key: string, label: string, required = false) => {
    const missing = required && isMissing(formValues[key]);
    const blocking = blockingKeySet.has(key) && isMissing(formValues[key]);
    return {
      required,
      validateStatus: (missing || blocking) ? ('warning' as const) : undefined,
      help: blocking ? `${label} 是当前阶段继续所需的信息` : undefined,
    };
  }, [blockingKeySet, formValues]);

  const anthropometricMetrics = useMemo(() => {
    const result: { bmi?: number; bsa?: number } = {};
    (intakeContext?.derived_covariates || []).forEach((item) => {
      if (item.key !== 'bmi' && item.key !== 'bsa') return;
      const numeric = Number(item.value);
      if (!Number.isFinite(numeric)) return;
      if (item.key === 'bmi') {
        result.bmi = numeric;
      } else {
        result.bsa = numeric;
      }
    });
    return result;
  }, [intakeContext?.derived_covariates]);

  const derivedAgeCard = intakeContext?.derived_age_context ? (
    <div className="patient-intake__card">
      <div className="patient-intake__card-head">
        <div>
          <div className="patient-intake__card-title">自动年龄衍生</div>
          <div className="patient-intake__card-desc">系统会根据出生日期、当前日期和胎龄自动推导年龄体系；人群标签仅供参考，实际以模型匹配为准。</div>
        </div>
        {intakeContext.reference_population ? (
          <Tag className="clinical-entity-tag" color={getPopulationTagColor(REFERENCE_POPULATION_LABELS[intakeContext.reference_population] || intakeContext.reference_population)}>{REFERENCE_POPULATION_LABELS[intakeContext.reference_population] || intakeContext.reference_population}</Tag>
        ) : null}
      </div>
      <div className="patient-intake__card-body">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {intakeContext.derived_age_context.age_years != null ? (
            <span className="patient-intake__summary-chip">年龄 <strong>{intakeContext.derived_age_context.age_years.toFixed(3)} 岁</strong></span>
          ) : null}
          {intakeContext.derived_age_context.age_months != null ? (
            <span className="patient-intake__summary-chip">月龄 <strong>{intakeContext.derived_age_context.age_months} 月</strong></span>
          ) : null}
          {intakeContext.derived_age_context.age_pna != null ? (
            <span className="patient-intake__summary-chip">出生后日龄 <strong>{intakeContext.derived_age_context.age_pna} 天</strong></span>
          ) : null}
          {intakeContext.derived_age_context.age_pma != null ? (
            <span className="patient-intake__summary-chip">PMA <strong>{intakeContext.derived_age_context.age_pma.toFixed(1)} 周</strong></span>
          ) : null}
          {intakeContext.derived_age_context.corrected_age_weeks != null ? (
            <span className="patient-intake__summary-chip">纠正年龄 <strong>{intakeContext.derived_age_context.corrected_age_weeks.toFixed(1)} 周</strong></span>
          ) : null}
        </div>
        {intakeContext.derived_age_context.should_collect_gestational_age ? (
          <div style={{ marginTop: 10 }}>
            <Text type="secondary">低龄患者建议补充胎龄，以便系统自动推导 PMA 并更准确地匹配新生儿/婴幼儿模型。</Text>
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  const derivedSection = (selectedDrug || intakeContext) ? (
    <div className="patient-intake__card">
      <div className="patient-intake__card-head">
        <div>
          <div className="patient-intake__card-title">衍生项</div>
          <div className="patient-intake__card-desc">系统会根据后台协变量定义、出生日期、当前日期与已录入指标自动推导年龄体系和可计算协变量，手工值优先保留。</div>
        </div>
        {intakeContext?.reference_population ? (
          <Tag className="clinical-entity-tag" color={getPopulationTagColor(REFERENCE_POPULATION_LABELS[intakeContext.reference_population] || intakeContext.reference_population)}>{REFERENCE_POPULATION_LABELS[intakeContext.reference_population] || intakeContext.reference_population}</Tag>
        ) : null}
      </div>
      <div className="patient-intake__card-body">
        {intakeContext?.derived_age_context ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {intakeContext.derived_age_context.age_years != null ? (
              <span className="patient-intake__summary-chip">年龄 <strong>{intakeContext.derived_age_context.age_years.toFixed(3)} 岁</strong></span>
            ) : null}
            {intakeContext.derived_age_context.age_months != null ? (
              <span className="patient-intake__summary-chip">月龄 <strong>{intakeContext.derived_age_context.age_months} 月</strong></span>
            ) : null}
            {intakeContext.derived_age_context.age_pna != null ? (
              <span className="patient-intake__summary-chip">出生后日龄 <strong>{intakeContext.derived_age_context.age_pna} 天</strong></span>
            ) : null}
            {intakeContext.derived_age_context.age_pma != null ? (
              <span className="patient-intake__summary-chip">PMA <strong>{intakeContext.derived_age_context.age_pma.toFixed(1)} 周</strong></span>
            ) : null}
            {intakeContext.derived_age_context.corrected_age_weeks != null ? (
              <span className="patient-intake__summary-chip">纠正年龄 <strong>{intakeContext.derived_age_context.corrected_age_weeks.toFixed(1)} 周</strong></span>
            ) : null}
          </div>
        ) : (
          <div className="patient-intake__empty">录入住院号、出生日期、当前日期、身高、体重等基础信息后，系统会自动推导年龄体系与可计算指标。</div>
        )}
        {intakeContext?.derived_age_context?.should_collect_gestational_age ? (
          <div style={{ marginTop: 10 }}>
            <Text type="secondary">低龄患者建议补录胎龄，系统会据此自动推导 PMA 并更准确地匹配新生儿/婴幼儿模型。</Text>
          </div>
        ) : null}
        {intakeContext?.derived_covariates?.length ? (
          <div style={{ marginTop: 14 }} className="patient-intake__derived-grid">
            {intakeContext.derived_covariates.map((item) => {
              const formattedValue = typeof item.value === 'number'
                ? item.value.toLocaleString('zh-CN', { maximumFractionDigits: 4 })
                : String(item.value);
              const formulaName = formatDerivedCovariateFormulaName(item);
              return (
                <div className="patient-intake__derived-item" key={item.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <div className="patient-intake__derived-label">
                      {item.unit ? `${item.label} (${item.unit})` : item.label}
                    </div>
                    <Tag color={item.source === 'provided' ? 'default' : 'green'}>
                      {item.source === 'provided' ? '已提供' : '自动计算'}
                    </Tag>
                  </div>
                  <div className="patient-intake__derived-value">{formattedValue}</div>
                  {formulaName ? (
                    <div className="patient-intake__derived-meta">公式名称：{formulaName}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  const renderField = (item: DisplayFieldItem) => {
    const definition = definitionFromItem(item, registry[item.key]);
    const placeholder = resolveCovariatePlaceholder(
      definition,
      definition.type === 'number' ? `请输入${definition.label}` : `请选择${definition.label}`,
    );
    const labelText = definition.unit ? `${definition.label} (${definition.unit})` : definition.label;
    const fieldSource = aiExtractionSnapshot?.field_sources?.[toAgentFieldKey(item.key)];
    const requirementTag = item.requirement_level === 'required_for_candidate_models'
      ? null
      : item.required
        ? { label: '必填', color: 'red' }
        : { label: '选填', color: 'default' };
    const label = (
      <Space size={4} wrap>
        <span>{labelText}</span>
        {requirementTag ? (
          <Tag color={requirementTag.color} style={{ marginInlineEnd: 0 }}>{requirementTag.label}</Tag>
        ) : null}
        {fieldSource === 'default' ? <Tag color="default" style={{ marginInlineEnd: 0 }}>默认</Tag> : null}
      </Space>
    );
    const rules = item.required ? [{ required: true, message: `请输入${definition.label}` }] : [];
    const missingBlocking = blockingKeySet.has(item.key) && isMissing(formValues[item.key]);
    const help = missingBlocking ? (
      <div className="patient-intake__field-note">
        <span className="patient-intake__field-note-text">{"\u8be5\u5b57\u6bb5\u9700\u8981\u5728\u5f53\u524d\u9636\u6bb5\u8865\u9f50\uff0c\u5b8c\u6210\u540e\u624d\u80fd\u7ee7\u7eed\u4e0b\u4e00\u6b65\u5f55\u5165\u3002"}</span>
      </div>
    ) : undefined;
    const commonProps = {
      name: item.key,
      label,
      rules,
      validateStatus: missingBlocking ? ('warning' as const) : undefined,
      help,
    };
    if (definition.type === 'boolean') {
      return (
        <Col xs={24} sm={12} lg={8} xl={6} key={item.key}>
          <Form.Item {...commonProps}>
            <Select allowClear placeholder={placeholder} options={[{ value: true, label: '是' }, { value: false, label: '否' }]} />
          </Form.Item>
        </Col>
      );
    }
    if (definition.type === 'select') {
      return (
        <Col xs={24} sm={12} lg={8} xl={6} key={item.key}>
          <Form.Item {...commonProps}>
            <Select allowClear placeholder={placeholder}>
              {(definition.options || []).map((option) => (
                <Option key={String(option.value)} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      );
    }
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={item.key}>
        <Form.Item {...commonProps}>
          <InputNumber
            style={{ width: '100%' }}
            min={definition.min}
            max={definition.max}
            step={definition.step || 0.1}
            placeholder={placeholder}
          />
        </Form.Item>
      </Col>
    );
  };

  const handleSubmit = async (values: Record<string, any>) => {
    if (!selectedDrug) return message.warning('请先选择目标药物');
    if (!values.patient_id) return message.warning('请先填写病例编号');
    if (!intakeContext) return message.warning('系统仍在准备录入上下文，请稍后重试');
    if (!intakeContext.ready_for_model_selection) {
      const labels = intakeContext.next_required_covariates.map((item) => item.label).filter(Boolean);
      return message.error(labels.length ? `当前还不能进入模型选择，请先补齐：${labels.join('、')}` : '当前还不能进入模型选择，请先完成本阶段录入。');
    }
    setSubmitting(true);
    try {
      let patient: Patient;
      try {
        patient = await patientAPI.search(values.patient_id);
        if (values.birth_date || values.gender) {
          patient = await patientAPI.update(patient.id, {
            birth_date: values.birth_date || undefined,
            gender: values.gender || undefined,
          });
        }
      } catch {
        patient = await patientAPI.create({
          patient_id: values.patient_id,
          birth_date: values.birth_date || undefined,
          gender: values.gender || undefined,
        });
      }
      const derivedAgeContext = intakeContext.derived_age_context;
      const explicitCovariateValues = Object.entries(values).reduce<Record<string, any>>((acc, [rawKey, value]) => {
        if (BASE_FIELD_KEYS.has(rawKey) || isMissing(value)) return acc;
        const formKey = toFormFieldKey(rawKey);
        const definition = registry[formKey] || registry[toAgentFieldKey(rawKey)];
        const normalizedValue = coerceCovariateValue(value, definition);
        if (normalizedValue !== undefined) {
          acc[formKey] = normalizedValue;
        }
        return acc;
      }, {});
      const derivedCovariateValues = (intakeContext.derived_covariates || []).reduce<Record<string, any>>((acc, item) => {
        const formKey = toFormFieldKey(item.key);
        const canonicalKey = toAgentFieldKey(item.key);
        const fieldSource = aiExtractionSnapshot?.field_sources?.[canonicalKey];
        const currentIsManual = manualFieldKeys.includes(canonicalKey) || fieldSource === 'manual' || fieldSource === 'confirmed_current';
        if (!isMissing(values[formKey]) && currentIsManual) return acc;
        const definition = registry[formKey] || registry[toAgentFieldKey(item.key)];
        const normalizedValue = coerceCovariateValue(item.value, definition);
        if (normalizedValue !== undefined) {
          acc[formKey] = normalizedValue;
        }
        return acc;
      }, {});
      const covariates = stripConflictingNeonatalCovariates({
        ...explicitCovariateValues,
        ...derivedCovariateValues,
        ...(derivedAgeContext?.age_pna != null ? { age_pna: derivedAgeContext.age_pna } : {}),
        ...(derivedAgeContext?.age_pma != null ? { age_pma: derivedAgeContext.age_pma } : {}),
        ...(derivedAgeContext?.age_years != null ? { age_years: derivedAgeContext.age_years } : {}),
        ...(derivedAgeContext?.age_months != null ? { age_months: derivedAgeContext.age_months } : {}),
        ...(derivedAgeContext?.corrected_age_weeks != null ? { corrected_age_weeks: derivedAgeContext.corrected_age_weeks } : {}),
      });
      const caseData = await caseAPI.create({
        patient_id: patient.id,
        drug_name: values.drug_name,
        diagnosis: values.diagnosis,
        covariates,
        ai_extraction_snapshot: {
          candidate_fields: [],
          recommended_apply_keys: [],
          applied_keys: [],
          field_patches: [],
          missing_required: intakeContext.next_required_covariates.map((item) => ({ key: item.key, label: item.label, source_tags: [] })),
          conflicts: activeConflictItems.map((item) => ({ key: item.key, reason: item.reason || '该字段需要确认' })),
          follow_up_questions: [],
          readiness_status: localReadinessStatus,
          field_sources: aiExtractionSnapshot?.field_sources || {},
          intake_context: intakeContext,
        },
      });
      onSubmit(patient, caseData);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBucketGrouped = (
    title: string,
    description: string,
    items: DisplayFieldItem[],
    options?: { tagColor?: string; collapsible?: boolean; collapsed?: boolean; onToggle?: () => void },
  ) => {
    if (!items.length) return null;
    const collapsed = Boolean(options?.collapsible && options?.collapsed);
    const numericItems = items.filter((item) => definitionFromItem(item, registry[item.key]).type === 'number');
    const categoricalItems = items.filter((item) => definitionFromItem(item, registry[item.key]).type !== 'number');
    const renderGroup = (_groupTitle: string, groupItems: DisplayFieldItem[]) => {
      if (!groupItems.length) return null;
      return <Row gutter={[10, 0]}>{groupItems.map((item) => renderField(item))}</Row>;
    };

    return (
      <div className="patient-intake__card" key={`${title}-grouped`}>
        <div className="patient-intake__card-head">
          <div>
            <div className="patient-intake__card-title">{title}</div>
            {description ? <div className="patient-intake__card-desc">{description}</div> : null}
          </div>
          <Space>
            <Tag color={options?.tagColor || 'default'}>{items.length} 项字段</Tag>
            {options?.collapsible ? (
              <Button className="patient-intake__toggle" icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={options.onToggle}>
                {collapsed ? '展开' : '收起'}
              </Button>
            ) : null}
          </Space>
        </div>
        {!collapsed && (
          <div className="patient-intake__card-body">
            {renderGroup('数值型指标', numericItems)}
            {renderGroup('分类型指标', categoricalItems)}
          </div>
        )}
      </div>
    );
  };

  const hasDynamicFields = bucketedFields.nextRequired.length || bucketedFields.conditional.length || bucketedFields.optional.length;
  const requiredFields = useMemo(
    () => [...bucketedFields.nextRequired, ...bucketedFields.conditional],
    [bucketedFields.conditional, bucketedFields.nextRequired],
  );

  return (
    <div className="patient-intake">
      <style>{STYLES}</style>

      {showSummary && (
        <div className="patient-intake__card">
          <div className="patient-intake__card-head">
            <div>
              <div className="patient-intake__card-title">{summaryStatus.title}</div>
              <div className="patient-intake__card-desc">{summaryStatus.description}</div>
            </div>
            <Tag color={summaryStatus.tagColor}>{summaryStatus.tagLabel}</Tag>
          </div>
          <div className="patient-intake__card-body">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <span className="patient-intake__summary-chip">当前阶段 <strong>{intakeContext?.stage_title || '基本信息'}</strong></span>
              <span className="patient-intake__summary-chip">完成度 <strong>{Math.round((intakeContext?.completion_ratio || 0) * 100)}%</strong></span>
              <span className="patient-intake__summary-chip">候选模型 <strong>{intakeContext?.candidate_models.length || 0}</strong></span>
              <span className="patient-intake__summary-chip">必填 <strong>{requiredFields.length}</strong></span>
              <span className="patient-intake__summary-chip">选填 <strong>{bucketedFields.optional.length}</strong></span>
            </div>
            {!!intakeContext?.next_required_covariates.length && (
              <div style={{ marginTop: 10 }}>
                <Space wrap>
                  {intakeContext.next_required_covariates.map((item) => (
                    <Tag key={item.key} color="red">{item.label}</Tag>
                  ))}
                </Space>
              </div>
            )}
          </div>
        </div>
      )}

      {contextError ? <Alert type="error" showIcon message={contextError} /> : null}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={(changedValues) => {
          const changedKeys = Object.keys(changedValues).map((key) => toAgentFieldKey(key));
          applyLinkedBasePatch(changedKeys);
          syncFormValues();
          if (!changedKeys.length) return;
          setManualFieldKeys((prev) => Array.from(new Set([...prev, ...changedKeys])));
        }}
      >
        <div className="patient-intake__card">
          <div className="patient-intake__card-head">
            <div>
              <div className="patient-intake__card-title">基本信息</div>
              <div className="patient-intake__card-desc">固定录入病例编号、目标药物与诊断，其余字段会按当前阶段动态展开。</div>
            </div>
            <Button className="patient-intake__agent" icon={<RobotOutlined />} onClick={() => openDrawer('extract')}>
              辅助识别/回填
            </Button>
          </div>
          <div className="patient-intake__card-body">
            <Row gutter={[10, 0]}>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item name="patient_id" label="住院号 / 病案号" rules={[{ required: true, message: '请输入住院号 / 病案号' }]}>
                  <Input placeholder="例如 H20260411-001" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="drug_name"
                  label="目标药物"
                  rules={[
                    { required: true, message: '请选择目标药物' },
                    {
                      validator: async (_, value) => {
                        if (!value || drugs.some((drug) => drug.drug_id === value)) return;
                        throw new Error('请选择后台已配置的目标药物');
                      },
                    },
                  ]}
                >
                  <Select showSearch optionFilterProp="children" allowClear loading={drugsLoading} placeholder="请选择目标药物">
                    {drugs.map((drug) => (
                      <Option key={drug.drug_id} value={drug.drug_id}>
                        {drug.drug_name}{drug.drug_name_en ? ` (${drug.drug_name_en})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="birth_date"
                  label="出生日期"
                  rules={[{ required: true, message: '请输入出生日期' }]}
                  validateStatus={baseFieldMeta('birth_date', '出生日期', true).validateStatus}
                  help={baseFieldMeta('birth_date', '出生日期', true).help}
                >
                  <Input type="date" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="current_date"
                  label="当前日期"
                  rules={[{ required: true, message: '请输入当前日期' }]}
                  validateStatus={baseFieldMeta('current_date', '当前日期', true).validateStatus}
                  help={baseFieldMeta('current_date', '当前日期', true).help}
                >
                  <Input type="date" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="age_years"
                  label="年龄 (岁)"
                  validateStatus={baseFieldMeta('age_years', '年龄').validateStatus}
                  help={baseFieldMeta('age_years', '年龄').help}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={120}
                    step={0.001}
                    placeholder={resolveCovariatePlaceholder(registry.age_years, '请输入年龄')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="gender"
                  label="性别"
                  validateStatus={baseFieldMeta('gender', '性别').validateStatus}
                  help={baseFieldMeta('gender', '性别').help}
                >
                  <Select allowClear placeholder={resolveCovariatePlaceholder(registry.gender, '请选择性别')}>
                    <Option value="male">男</Option>
                    <Option value="female">女</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="height"
                  label="身高 (cm)"
                  validateStatus={baseFieldMeta('height', '身高').validateStatus}
                  help={baseFieldMeta('height', '身高').help}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={20}
                    max={260}
                    step={0.1}
                    placeholder={resolveCovariatePlaceholder(registry.height, '请输入身高')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} xl={6}>
                <Form.Item
                  name="weight"
                  label="体重 (kg)"
                  rules={[{ required: true, message: '请输入体重' }]}
                  validateStatus={baseFieldMeta('weight', '体重', true).validateStatus}
                  help={baseFieldMeta('weight', '体重', true).help}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0.2}
                    max={500}
                    step={0.1}
                    placeholder={resolveCovariatePlaceholder(registry.weight, '请输入体重')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="diagnosis" label="诊断">
                  <Input placeholder="请输入主要诊断或临床问题" />
                </Form.Item>
              </Col>
            </Row>
            {(anthropometricMetrics.bmi != null || anthropometricMetrics.bsa != null) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {anthropometricMetrics.bmi != null ? (
                  <span className="patient-intake__summary-chip">BMI <strong>{anthropometricMetrics.bmi.toFixed(1)}</strong></span>
                ) : null}
                {anthropometricMetrics.bsa != null ? (
                  <span className="patient-intake__summary-chip">BSA <strong>{anthropometricMetrics.bsa.toFixed(3)} m²</strong></span>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {renderBucketGrouped('必填项', '', requiredFields, { tagColor: 'red' })}
        {renderBucketGrouped('选填项', '', bucketedFields.optional, {
          tagColor: 'default',
          collapsible: true,
          collapsed: !showMoreFields,
          onToggle: () => setShowMoreFields((prev) => !prev),
        })}

        {derivedSection}

        {false && (
        <div className="patient-intake__card">
          <div className="patient-intake__card-head">
            <div>
              <div className="patient-intake__card-title">手工补录字段</div>
              <div className="patient-intake__card-desc">如果当前阶段未自动展开你要录入的协变量，可以从后台配置字段中手工选择并立即填写。</div>
            </div>
          </div>
          <div className="patient-intake__card-body">
            {availableManualExtraOptions.length ? (
              <>
                <div style={{ marginBottom: 10 }}>
                  <Text type="secondary">
                    当前可补录 {availableManualExtraOptions.length} 项。先在下拉框里选择你要补录的指标，选中后，对应输入框会在“更多字段”区域展开。
                  </Text>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Space wrap>
                    {manualExtraPreviewOptions.map((option) => (
                      <Tag key={String(option.value)}>{option.label}</Tag>
                    ))}
                    {availableManualExtraOptions.length > manualExtraPreviewOptions.length ? (
                      <Tag>+{availableManualExtraOptions.length - manualExtraPreviewOptions.length} 项</Tag>
                    ) : null}
                  </Space>
                </div>
              </>
            ) : (
              <div className="patient-intake__empty" style={{ marginBottom: 12 }}>
                当前阶段没有额外可补录的字段；如果后续模型需要新的可选指标，系统会自动把它们放到这里。
              </div>
            )}
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              style={{ width: '100%' }}
              placeholder="选择要手工补录的协变量"
              value={manualExtraFieldKeys}
              options={availableManualExtraOptions}
              disabled={!availableManualExtraOptions.length}
              notFoundContent="当前没有可补录指标"
              onChange={(values) => setManualExtraFieldKeys(values as string[])}
            />
            <div style={{ marginTop: 10 }}>
              <Text type="secondary">
                {manualExtraFieldKeys.length
                  ? `已选择 ${manualExtraFieldKeys.length} 项补录字段，请到“更多字段”中填写。`
                  : '如果没有额外指标需要补录，可以忽略这个模块，直接继续下一步。'}
              </Text>
            </div>
          </div>
        </div>
        )}

        {selectedDrug && !hasDynamicFields ? (
          <div className="patient-intake__card">
            <div className="patient-intake__card-head">
              <div>
                <div className="patient-intake__card-title">动态字段</div>
                <div className="patient-intake__card-desc">当前尚未生成额外动态字段，继续录入后系统会自动刷新。</div>
              </div>
            </div>
            <div className="patient-intake__card-body">
              <div className="patient-intake__empty">暂未加载到当前病例所需的动态协变量，继续录入即可。</div>
            </div>
          </div>
        ) : null}

        <div className="patient-intake__actions">
          <Button className="patient-intake__clear" onClick={onClearWorkspace} disabled={submitting}>清空页面</Button>
          <Button type="primary" htmlType="submit" loading={submitting} disabled={!canSubmit} className="patient-intake__submit">
            {canSubmit ? '继续进入模型选择' : '完成当前阶段后继续'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PatientForm;
